import { memo, useCallback } from 'react';

import { BookIcon, CircleHelpIcon, ExternalLinkIcon, SignalIcon, HeadsetIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useWebClient } from '@/hooks/useWebClient';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import {
  MOVEO_CHANNELS_WS_URL,
  MOVEO_INTEGRATION_ID,
  STATUS_URL,
  SUPPORT_URL,
} from '@/util/constants';

const linkClassName = 'flex items-center gap-2 text-inherit no-underline';

export const HelpMenu = memo(() => {
  const { t } = useTranslation();

  const { initialiseWebchat } = useWebClient({
    // Host is hardcoded to AWS Production since the `integrationId` is
    // available only in production
    host: MOVEO_CHANNELS_WS_URL,
    integrationId: MOVEO_INTEGRATION_ID,
    launcher: { show: false },
  });

  const handleOpenStatus = useCallback(() => {
    trackEvent(EventName.ClickPlatformStatus);
  }, []);

  const handleOpenDocs = useCallback(() => {
    trackEvent(EventName.ClickDocumentation);
  }, []);

  const handleOpenWebchat = useCallback(() => {
    initialiseWebchat();
  }, [initialiseWebchat]);

  const handleClick = useCallback(() => {
    trackEvent(EventName.OpenHelpMenu);
  }, []);

  return (
    <DropdownMenu>
      <InfoTooltip content={t('main_navigation.help_menu')} delayDuration={200}>
        <DropdownMenuTrigger asChild>
          <Button
            id="help-button"
            aria-label={t('main_navigation.help_menu')}
            className="h-8 w-8 rounded hover:bg-white/16 [&_svg]:size-5 [&_svg]:p-0 [&_svg]:text-navigation-element"
            variant="ghost-neutral"
            size="icon-md"
            onClick={handleClick}
          >
            <CircleHelpIcon size={20} />
          </Button>
        </DropdownMenuTrigger>
      </InfoTooltip>
      <DropdownMenuContent
        className="w-56"
        align="end"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem asChild>
          <a
            href={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOpenDocs}
            className={linkClassName}
          >
            <BookIcon size={16} className="text-foreground-muted" />
            {t('docs.docs')}
            <ExternalLinkIcon size={12} className="ml-auto text-foreground-muted" />
          </a>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <a
            href={STATUS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleOpenStatus}
            className={linkClassName}
          >
            <SignalIcon size={16} className="text-foreground-muted" />
            {t('docs.platform_status')}
            <ExternalLinkIcon size={12} className="ml-auto text-foreground-muted" />
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleOpenWebchat} className="flex items-center gap-2">
          <HeadsetIcon size={16} className="text-foreground-muted" />
          {t('home.contact_support')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

HelpMenu.displayName = 'HelpMenu';
